/**
 * Centralized auth environment + SSO referrer derivation.
 *
 * The canonical SSO host is `signin.stackline.com` (and
 * `signin-localhost.stackline.com` locally). The previous host,
 * `enterprise-sso.stackline.com` / `enterprise-sso-localhost.stackline.com`,
 * is retained as an accepted alias for backward compatibility — AWS serves
 * both from the same CloudFront distribution, and all SSO-host detection
 * here recognizes both names via `isSsoHost()`.
 *
 * Routing is purely hostname/referrer-driven (no toggles):
 *   - *-localhost.stackline.com → signin-localhost + api-localhost
 *   - *-qa.stackline.com        → signin (prod)    + api-qa
 *   - *-dev.stackline.com       → signin (prod)    + api-dev
 *   - bare *.stackline.com      → signin (prod)    + api.stackline.com
 *
 * This matches the pre-existing repo convention in `buildBaseTarget`
 * (src/utils/app.ts) which has always routed any localhost host through
 * api-localhost.stackline.com so V1 session cookies set on the API host
 * are visible across every app subdomain.
 *
 * Working locally therefore implies you have api-localhost.stackline.com
 * resolving to your local API service.
 */

/**
 * Exact hostnames that serve the SSO app. Uses a Set for O(1) lookups
 * and precise matching (no substring ambiguity with `atlas.stackline.com/signin`
 * as a path, or any future unrelated `*-signin.stackline.com` hostname).
 */
const SSO_HOSTS: ReadonlySet<string> = new Set([
  'signin.stackline.com',
  'signin-localhost.stackline.com',
  'enterprise-sso.stackline.com',
  'enterprise-sso-localhost.stackline.com'
]);

/**
 * Returns true iff `host` is one of the exact SSO hostnames (new or legacy).
 * Accepts optional port suffix and any casing.
 */
export function isSsoHost(host: string | undefined | null): boolean {
  if (!host) return false;
  return SSO_HOSTS.has(host.toLowerCase().split(':')[0]);
}

/**
 * Auth stage of the originating app. `beta` and `gamma` are first-class
 * stages (ShopperOS / ShopperOS V2-specific today — see `APPS_WITH_BETA` / `APPS_WITH_GAMMA`)
 * rather than being folded into `dev`, so per-app deployment availability
 * can be resolved correctly when building cross-app redirect URLs.
 */
export type AppStage = 'prod' | 'dev' | 'qa' | 'localhost' | 'beta' | 'gamma';

export interface ReferrerInfo {
  appName: string;
  stage: AppStage;
  referrer: string;
}

/**
 * Hostname patterns that map to specific apps.
 * Order matters - more specific patterns should come first.
 */
const HOSTNAME_TO_APP: Record<string, string> = {
  // `admanager` must come before `ad-` because Object.entries iteration order
  // matches insertion order and the loop short-circuits on first match --
  // otherwise `admanager-dev.stackline.com` would resolve to the legacy `ad`
  // app instead of admanager.
  admanager: 'admanager',
  advisor: 'advisor',
  zenith: 'advisor',
  atlas: 'atlas',
  beacon: 'beacon',
  omni: 'omni',
  discover: 'discover',
  drive: 'ad',
  'ad-': 'ad',
  shopperos: 'shopperos',
  shopperanalytics: 'shopperanalytics',
  'digital-shelf': 'digital-shelf',
  sync: 'sync',
  'browser-registry': 'browser-registry',
  // The SSO host (`signin.stackline.com` / legacy `enterprise-sso.stackline.com`)
  // serves the sign-in UI itself, not an end-user app. When a visitor lands
  // there with no `?referrer` URL param (no origin app context), default to
  // Advisor so the post-login redirect sends them to advisor.stackline.com —
  // matching the default Advisor branding already rendered on the sign-in
  // page (see `getAppName` in src/utils/app.ts). Without this, the fallback
  // `return 'atlas'` below would send them to Atlas, which doesn't match
  // what the UI shows.
  signin: 'advisor',
  'enterprise-sso': 'advisor'
};

/**
 * Determine the auth stage from a hostname.
 *   - localhost / *-localhost.stackline.com → 'localhost'
 *   - *-qa.stackline.com → 'qa'
 *   - *-gamma.stackline.com → 'gamma'
 *   - *-beta.stackline.com → 'beta'
 *   - *-dev/-alpha/-staging.stackline.com → 'dev'
 *   - bare *.stackline.com → 'prod'
 *
 * `beta` and `gamma` are ShopperOS / ShopperOS V2-specific stages today (omni also ships
 * a gamma). They are NOT folded into `dev` here so downstream consumers
 * (no-access fallback, app-URL builders) can resolve per-app deployment
 * availability via `resolveStageForApp`. `getApiHost` still routes both
 * to `api-dev.stackline.com` — they share the dev API backend.
 */
export function getStageFromHostname(hostname: string): AppStage {
  if (hostname.includes('localhost') || hostname.includes('-localhost')) {
    return 'localhost';
  }
  if (/-qa(?:[.-]|$)/.test(hostname)) {
    return 'qa';
  }
  if (/-gamma(?:[.-]|$)/.test(hostname)) {
    return 'gamma';
  }
  if (/-beta(?:[.-]|$)/.test(hostname)) {
    return 'beta';
  }
  const isProd = hostname.includes('.stackline.com') && !/-(?:dev|alpha|staging)/.test(hostname);
  return isProd ? 'prod' : 'dev';
}

/**
 * Extract the app name from a hostname.
 */
export function getAppNameFromHostname(hostname: string): string {
  const lowerHostname = hostname.toLowerCase();

  for (const [pattern, appName] of Object.entries(HOSTNAME_TO_APP)) {
    if (lowerHostname.includes(pattern)) {
      return appName;
    }
  }

  return 'atlas';
}

/**
 * Build the SSO referrer string from app name and stage.
 * Prod referrers have no suffix (e.g. 'atlas'), dev referrers get '-dev'.
 */
export function buildReferrer(appName: string, stage: AppStage): string {
  // `enterprise-sso` historically skipped the stage suffix. Kept as a
  // defensive no-op — in practice `HOSTNAME_TO_APP` remaps both SSO hosts
  // to `advisor`, so this branch is unreachable in normal flow.
  if (stage === 'prod' || appName === 'enterprise-sso') {
    return appName;
  }
  return `${appName}-${stage}`;
}

/**
 * Apps that have their own `-gamma` / `-beta` deployment today. `gamma`
 * and `beta` are stages used by ShopperOS / ShopperOS V2 / Shopper Analytics / Digital
 * Shelf (omni also ships a gamma). Atlas / Beacon / Advisor / Ad Manager
 * only have prod and dev.
 *
 * When a user from a `*-gamma` / `*-beta` session needs to be bounced to
 * a different app (no-access fallback) or to the same app in a different
 * division, this map lets callers fall back to prod for apps that don't
 * have a matching stage deployment, instead of building dead hostnames
 * like `atlas-gamma.stackline.com`.
 */
const APPS_WITH_GAMMA: ReadonlySet<string> = new Set(['shopperos', 'shopperosv2', 'omni', 'shopperanalytics']);
const APPS_WITH_BETA: ReadonlySet<string> = new Set(['shopperos', 'shopperosv2', 'shopperanalytics', 'digital-shelf']);

/**
 * Resolve the effective stage for a given app — falls back to `'prod'`
 * when the requested `beta` / `gamma` stage has no deployment for the
 * target app. All other stages pass through unchanged.
 */
export function resolveStageForApp(app: string, stage: AppStage): AppStage {
  if (stage === 'gamma' && !APPS_WITH_GAMMA.has(app)) return 'prod';
  if (stage === 'beta' && !APPS_WITH_BETA.has(app)) return 'prod';
  return stage;
}

/**
 * Get complete referrer info from a hostname.
 */
export function getReferrerInfo(hostname?: string): ReferrerInfo {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const appName = getAppNameFromHostname(host);
  const stage = getStageFromHostname(host);
  const referrer = buildReferrer(appName, stage);

  return { appName, stage, referrer };
}

/**
 * Get the SSO referrer string from the current hostname.
 */
export function getSsoReferrer(hostname?: string): string {
  return getReferrerInfo(hostname).referrer;
}

/**
 * Get the canonical SSO hostname.
 * Local frontends route through signin-localhost (served by the same
 * webpack dev server via nginx). All other environments use the single
 * shared prod signin.stackline.com deployment (backward-compat alias:
 * enterprise-sso.stackline.com).
 */
export function getSsoHost(): string {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname.includes('localhost')) return 'signin-localhost.stackline.com';
  return 'signin.stackline.com';
}

/**
 * @deprecated use `getSsoHost()` — kept as a thin alias for callers that
 * have not yet been renamed. Returns the same value.
 */
export function getEnterpriseSsoHost(): string {
  return getSsoHost();
}

/**
 * Build the URL the app navigates to when the active division doesn't grant
 * `appAccess[appName]`. Appends `?stage=<dev|qa|localhost|beta|gamma>` so
 * the SSO's `NoAccessRedirect` can route the user back to the matching
 * stage after picking an accessible app. Prod traffic omits the param
 * (backward compat).
 *
 * The SSO host is shared between dev and prod (`signin.stackline.com`), so
 * without this hint `NoAccessRedirect` could only infer prod from its own
 * hostname and would send non-prod users to prod apps. See the consumer in
 * `src/components/SignIn/NoAccessRedirect.tsx`.
 */
export function buildNoAccessUrl(appName: string, divisionName: string, hostname?: string): string {
  const ssoHost = getSsoHost();
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const stage = getStageFromHostname(host);
  const params = new URLSearchParams({
    app: appName,
    divName: divisionName || 'your division'
  });
  if (stage !== 'prod') params.set('stage', stage);
  return `https://${ssoHost}/no-access?${params.toString()}`;
}

/**
 * Get the Stackline API host for auth V1 + V2 calls.
 *
 * On the SSO host (signin.stackline.com or the legacy
 * enterprise-sso.stackline.com alias) the page hostname carries no env
 * signal — the SSO app is a single shared prod deployment (or local dev).
 * In that case we derive the stage from the referrer (URL param, then
 * localStorage), which already encodes the originating app's stage (e.g.
 * "atlas-dev" → dev). Falls back to the page hostname for normal app
 * domains.
 */
export function getApiHost(hostname?: string): string {
  let host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  if (typeof window !== 'undefined' && isSsoHost(host)) {
    const referrer = resolveReferrerForApiHost();
    if (referrer) {
      host = `${referrer}.stackline.com`;
    }
  }

  const { stage } = getReferrerInfo(host);
  if (stage === 'localhost') return 'api-localhost.stackline.com';
  if (stage === 'qa') return 'api-qa.stackline.com';
  // `beta` and `gamma` have no dedicated API backend — they share api-dev,
  // matching the brandclub shopperos client's own `getStacklineApiHost()`
  // so the OTP/refresh cookie is minted and validated on the same backend.
  if (stage === 'dev' || stage === 'beta') return 'api-dev.stackline.com';
  // both gamma and prod, should use api.stackline.com (prod because we're on prod sso)
  return 'api.stackline.com';
}

function resolveReferrerForApiHost(): string | null {
  try {
    const urlReferrer = new URLSearchParams(window.location.search).get('referrer');
    if (urlReferrer) return urlReferrer;
    return getAuthFlowValue('x-sl-sso-signin-referrer');
  } catch {
    return null;
  }
}

/**
 * Referrers where the hostname doesn't follow the standard {referrer}.stackline.com pattern.
 */
const REFERRER_HOSTNAME_OVERRIDES: Record<string, string> = {
  'advisor-localhost': 'zenith-localhost.stackline.com',
  gitbook: 'enterprise-help.stackline.com',
  'stackline-support-portal': 'enterprise-help.stackline.com',
  // Sync uses a `*.sync.stackline.com` subdomain pattern instead of the
  // standard `sync*.stackline.com`. Stage is encoded as a `dev-` prefix on
  // the leftmost label (`dev-console.sync...`), not a `-dev` suffix. The
  // `sync-dev` / `sync-localhost` referrers both resolve to the dev console
  // host -- Sync local dev points at the dev API, so localhost shares it.
  sync: 'console.sync.stackline.com',
  'sync-dev': 'dev-console.sync.stackline.com',
  'sync-localhost': 'dev-console.sync.stackline.com'
};

/**
 * Map referrer prefixes to the hostnames they should redirect to.
 * Used to validate that returnUrl belongs to the correct app.
 */
const REFERRER_TO_VALID_HOSTS: Record<string, string[]> = {
  advisor: ['zenith', 'advisor'],
  atlas: ['atlas'],
  beacon: ['beacon'],
  omni: ['omni'],
  discover: ['discover'],
  ad: ['ad', 'drive'],
  admanager: ['admanager'],
  sync: ['sync'],
  'browser-registry': ['browser-registry'],
  'digital-shelf': ['digital-shelf'],
  shopperanalytics: ['shopperanalytics'],
  // `enterprise-sso` is kept only as a historical self-referrer entry. No
  // `signin` entry on purpose — a user who just signed in should never be
  // redirected back to the sign-in page, and accepting a `returnUrl=signin…`
  // would open a redirect loop / host-confusion vector.
  'enterprise-sso': ['enterprise-sso'],
  gitbook: ['enterprise-help'],
  'stackline-support-portal': ['enterprise-help']
};

/**
 * Validate that a returnUrl belongs to the same app as the referrer.
 * Prevents cross-app redirects from stale localStorage data.
 */
function isReturnUrlValidForReferrer(referrer: string, returnUrl: string): boolean {
  try {
    const url = new URL(decodeURIComponent(returnUrl));
    const hostname = url.hostname.toLowerCase();

    const referrerBase = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
    const validHosts = REFERRER_TO_VALID_HOSTS[referrerBase] || [referrerBase];

    return validHosts.some((host) => hostname.includes(host));
  } catch {
    return false;
  }
}

/**
 * Get the target app URL for a referrer string.
 * Used by the SSO app to redirect back to the originating app after login/division-switch.
 */
export function getAppUrlForReferrer(referrer: string, returnUrl?: string): string {
  if (returnUrl && isReturnUrlValidForReferrer(referrer, returnUrl)) {
    return decodeURIComponent(returnUrl);
  }

  if (returnUrl && !isReturnUrlValidForReferrer(referrer, returnUrl)) {
    console.warn('[SSO] Ignoring returnUrl that does not match referrer:', { referrer, returnUrl });
  }

  const baseReferrer = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
  const override = REFERRER_HOSTNAME_OVERRIDES[referrer] || REFERRER_HOSTNAME_OVERRIDES[baseReferrer];
  if (override) {
    return `https://${override}`;
  }

  return `https://${referrer}.stackline.com`;
}

/**
 * Build complete SSO sign-in URL.
 *
 * The canonical SSO host (`signin.stackline.com`) renders the sign-in form
 * on the root path — `/sign_in` is redundant when the hostname already
 * encodes sign-in intent. For backward compatibility the app still accepts
 * `/sign_in` on both signin and the legacy enterprise-sso host.
 */
export function buildSsoSignInUrl(returnUrl?: string, hostname?: string): string {
  const referrer = getSsoReferrer(hostname);
  const ssoHost = getSsoHost();
  const encodedReturn = returnUrl ? encodeURIComponent(returnUrl) : '';

  return `https://${ssoHost}/?referrer=${referrer}${encodedReturn ? `&returnUrl=${encodedReturn}` : ''}`;
}

// ---------------------------------------------------------------------------
// TTL-based localStorage helpers for auth flow data (referrer, returnUrl).
// Values expire after 10 minutes to prevent stale redirects.
// ---------------------------------------------------------------------------

const AUTH_FLOW_TTL_MS = 10 * 60 * 1000;

export function setAuthFlowValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, JSON.stringify({ value, ts: Date.now() }));
  } catch {
    /* quota exceeded or private browsing */
  }
}

export function getAuthFlowValue(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed.ts) {
      if (Date.now() - parsed.ts > AUTH_FLOW_TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    }
    return raw;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function clearAuthFlowData(): void {
  try {
    localStorage.removeItem('x-sl-sso-signin-referrer');
    localStorage.removeItem('x-sl-sso-signin-return-url');
    localStorage.removeItem('x-sl-sso-signin-redirect-url');
  } catch {
    /* ignore */
  }
}
